import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DeleteOutline, EditOutlined, Save } from "@mui/icons-material";

import { API_URL } from "../helpers/constants";
import {
  brandButton,
  darkSurface,
  glassCard,
  heroText,
  motionVariants,
  pageBackground,
} from "../helpers/designSystem";

/**
 * SYSTEM-Admin platform limits editor.
 *
 * Two sections, both editing the DB layers added in coordinator#172:
 *
 *   1. Platform defaults — every known key with its current effective
 *      value. Edit inline → PUT /api/admin/limits/{key}.
 *
 *   2. Per-account overrides — pick an account from the dropdown,
 *      see/set/clear overrides for it. PUT /api/admin/accounts/{id}/
 *      overrides/{key} OR DELETE to drop back to platform default.
 *
 * Route: /platform-limits — SYSTEM-only (gated in App.js via
 * ProtectedRoutes requiredRole + server-side requireSystem in
 * LimitsService).
 */
export default function PlatformLimits() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const token = (() => { try { return sessionStorage.getItem("token"); } catch { return null; } })();
  const authHdrs = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const [platformLimits, setPlatformLimits] = useState(null);
  const [error, setError] = useState(null);
  const [editKey, setEditKey] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [accountLimits, setAccountLimits] = useState(null);
  const [accountOverrides, setAccountOverrides] = useState([]);
  const [accountDetailLoading, setAccountDetailLoading] = useState(false);
  const [accountDetailError, setAccountDetailError] = useState(null);
  const [overrideEditing, setOverrideEditing] = useState(null);

  // Race guard: rapid A→B→C selection can resolve out of order; only
  // the most recent request is allowed to write state.
  const accountReqId = useRef(0);

  const loadPlatform = useCallback(() => {
    fetch(`${API_URL}/admin/limits`, { headers: authHdrs })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setPlatformLimits)
      .catch((err) => setError(err.message));
  }, [authHdrs]);

  const loadAccounts = useCallback(() => {
    fetch(`${API_URL}/admin/accounts`, { headers: authHdrs })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((list) => setAccounts(Array.isArray(list) ? list : []))
      .catch((err) => setError(`Failed to load accounts: ${err.message}`));
  }, [authHdrs]);

  const loadAccountDetail = useCallback((accountId) => {
    const reqId = ++accountReqId.current;
    setAccountLimits(null);
    setAccountOverrides([]);
    setAccountDetailError(null);
    if (!accountId) { setAccountDetailLoading(false); return; }
    setAccountDetailLoading(true);
    Promise.all([
      fetch(`${API_URL}/admin/accounts/${accountId}/limits`, { headers: authHdrs })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))),
      fetch(`${API_URL}/admin/accounts/${accountId}/overrides`, { headers: authHdrs })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))),
    ])
      .then(([lim, ovr]) => {
        if (reqId !== accountReqId.current) return;
        setAccountLimits(lim);
        setAccountOverrides(Array.isArray(ovr) ? ovr : []);
      })
      .catch((err) => {
        if (reqId !== accountReqId.current) return;
        setAccountDetailError(err.message);
      })
      .finally(() => {
        if (reqId !== accountReqId.current) return;
        setAccountDetailLoading(false);
      });
  }, [authHdrs]);

  const handleClearOverride = useCallback((accountId, key) => {
    fetch(`${API_URL}/admin/accounts/${accountId}/overrides/${key}`, {
      method: "DELETE",
      headers: authHdrs,
    })
      .then((r) => (r.ok
        ? loadAccountDetail(accountId)
        : r.text().then((t) => Promise.reject(new Error(t || `HTTP ${r.status}`)))))
      .catch((err) => setAccountDetailError(`Failed to clear override: ${err.message}`));
  }, [authHdrs, loadAccountDetail]);

  useEffect(() => { loadPlatform(); loadAccounts(); }, [loadPlatform, loadAccounts]);
  useEffect(() => { loadAccountDetail(selectedAccountId); }, [selectedAccountId, loadAccountDetail]);

  return (
    <Box sx={{ ...pageBackground(isDarkMode), mx: -3, mt: -3, px: 3, pt: 3, pb: 4 }}>
      <Box
        component={motion.div}
        initial="initial"
        animate="animate"
        variants={motionVariants.heroEnter}
        sx={{ mb: 4 }}
      >
        <Typography variant="h3" sx={{ ...heroText(isDarkMode), fontSize: { xs: "1.75rem", md: "2.25rem" }, mb: 0.5 }}>
          Platform limits
        </Typography>
        <Typography variant="body1" sx={{ color: isDarkMode ? darkSurface.textMuted : "#475569" }}>
          Tune trial-tier resource limits across the platform, or carve out
          per-account exceptions for design partners and paid plans.
        </Typography>
      </Box>

      {error && (
        <Box sx={{ p: 2, mb: 3, ...glassCard(isDarkMode), borderColor: "rgba(239,68,68,0.4)" }}>
          <Typography sx={{ color: "#fca5a5" }}>{error}</Typography>
        </Box>
      )}

      <Typography variant="h6" sx={{ mb: 1.5, color: isDarkMode ? darkSurface.text : "#0f172a" }}>
        Platform defaults
      </Typography>
      <TableContainer sx={{ ...glassCard(isDarkMode), mb: 5, overflow: "hidden" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: isDarkMode ? "rgba(255,255,255,0.04)" : "rgba(234,88,12,0.06)" }}>
              <TableCell sx={{ fontWeight: 700 }}>Limit</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Value</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {!platformLimits && (
              <TableRow><TableCell colSpan={3} sx={{ textAlign: "center", color: isDarkMode ? darkSurface.textMuted : "#64748b" }}>Loading…</TableCell></TableRow>
            )}
            {platformLimits && Object.entries(platformLimits).map(([key, value]) => (
              <TableRow key={key} hover>
                <TableCell>{prettyKey(key)}<Box sx={{ fontSize: "0.7rem", fontFamily: "monospace", color: isDarkMode ? darkSurface.textFaint : "#94a3b8" }}>{key}</Box></TableCell>
                <TableCell>{value}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit platform default">
                    <IconButton
                      size="small"
                      sx={{ color: "#c2410c" }}
                      onClick={() => setEditKey({ key, current: value })}
                    >
                      <EditOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 1.5, color: isDarkMode ? darkSurface.text : "#0f172a" }}>
        Per-account overrides
      </Typography>
      <Box sx={{ ...glassCard(isDarkMode), p: 3, mb: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <Typography variant="body2">Account:</Typography>
          <Select
            size="small"
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
            displayEmpty
            sx={{ minWidth: 320 }}
          >
            <MenuItem value=""><em>— pick an account —</em></MenuItem>
            {accounts.map((a) => (
              <MenuItem key={a.id} value={a.id}>{a.name || a.id} {a.billingEnabled ? "" : "· trial"}</MenuItem>
            ))}
          </Select>
        </Stack>

        {accountDetailError && (
          <Box sx={{ p: 2, mt: 2, ...glassCard(isDarkMode), borderColor: "rgba(239,68,68,0.4)" }}>
            <Typography sx={{ color: "#fca5a5" }}>{accountDetailError}</Typography>
          </Box>
        )}

        {!selectedAccountId && !accountDetailError && (
          <Box sx={{ mt: 3, py: 4, textAlign: "center", color: isDarkMode ? darkSurface.textMuted : "#64748b" }}>
            Select an account above to view and edit its overrides.
          </Box>
        )}

        {selectedAccountId && (accountDetailLoading || accountLimits) && (
          <TableContainer sx={{ mt: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Limit</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Effective</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Override</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {accountDetailLoading && (
                  <TableRow><TableCell colSpan={4} sx={{ textAlign: "center", color: isDarkMode ? darkSurface.textMuted : "#64748b" }}>Loading…</TableCell></TableRow>
                )}
                {!accountDetailLoading && accountLimits && Object.entries(accountLimits).map(([key, value]) => {
                  const override = accountOverrides.find((o) => o.limitKey === key);
                  return (
                    <TableRow key={key} hover>
                      <TableCell>{prettyKey(key)}<Box sx={{ fontSize: "0.7rem", fontFamily: "monospace", color: isDarkMode ? darkSurface.textFaint : "#94a3b8" }}>{key}</Box></TableCell>
                      <TableCell>{value}</TableCell>
                      <TableCell>
                        {override ? (
                          <Tooltip title={override.reason || "(no reason recorded)"}>
                            <Chip
                              label={`Override: ${override.limitValue}`}
                              size="small"
                              sx={{ background: "rgba(234,88,12,0.14)", color: "#c2410c", border: "1px solid rgba(234,88,12,0.35)" }}
                            />
                          </Tooltip>
                        ) : (
                          <Box sx={{ color: isDarkMode ? darkSurface.textFaint : "#94a3b8", fontSize: "0.78rem" }}>using platform default</Box>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={override ? "Update override" : "Set override"}>
                          <IconButton
                            size="small"
                            sx={{ color: "#c2410c" }}
                            onClick={() => setOverrideEditing({ key, current: override?.limitValue ?? value })}
                          >
                            <EditOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {override && (
                          <Tooltip title="Clear override — revert to platform default">
                            <IconButton
                              size="small"
                              sx={{ color: "#ef4444" }}
                              onClick={() => handleClearOverride(selectedAccountId, key)}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {editKey && (
        <EditValueDialog
          title={`Set platform default — ${prettyKey(editKey.key)}`}
          keyName={editKey.key}
          current={editKey.current}
          authHdrs={authHdrs}
          onClose={() => setEditKey(null)}
          onSaved={() => { setEditKey(null); loadPlatform(); }}
          urlBuilder={(key) => `${API_URL}/admin/limits/${key}`}
        />
      )}
      {overrideEditing && (
        <EditValueDialog
          title={`Override — ${prettyKey(overrideEditing.key)}`}
          keyName={overrideEditing.key}
          current={overrideEditing.current}
          allowReason
          authHdrs={authHdrs}
          onClose={() => setOverrideEditing(null)}
          onSaved={() => { setOverrideEditing(null); loadAccountDetail(selectedAccountId); }}
          urlBuilder={(key) => `${API_URL}/admin/accounts/${selectedAccountId}/overrides/${key}`}
        />
      )}
    </Box>
  );
}

function EditValueDialog({ title, keyName, current, authHdrs, onClose, onSaved, urlBuilder, allowReason }) {
  const [value, setValue] = useState(String(current ?? ""));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = () => {
    const n = parseInt(value, 10);
    if (Number.isNaN(n) || n < 0) { setErr("Value must be a non-negative integer"); return; }
    setBusy(true);
    setErr(null);
    fetch(urlBuilder(keyName), {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHdrs },
      body: JSON.stringify(allowReason ? { value: n, reason } : { value: n }),
    })
      .then((r) => (r.ok ? r.json() : r.text().then((t) => Promise.reject(new Error(t || `HTTP ${r.status}`)))))
      .then(onSaved)
      .catch((e) => setErr(e.message))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Value" type="number" fullWidth value={value} onChange={(e) => setValue(e.target.value)} inputProps={{ min: 0 }} />
          {allowReason && (
            <TextField label="Reason (optional)" fullWidth multiline minRows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. design partner — 3 projects for Acme until 2026-12-31" />
          )}
          {err && <Typography color="error" variant="body2">{err}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" startIcon={<Save />} onClick={submit} disabled={busy} sx={brandButton()}>
          {busy ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function prettyKey(k) {
  if (!k) return k;
  const tail = k.split(".").pop().replace(/_/g, " ");
  return tail.charAt(0).toUpperCase() + tail.slice(1);
}
